import { observer } from 'mobx-react-lite';

import { Button } from '@/components/ui';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { ProgressBar } from '@/components/ui/ProgressBar';
import { Skeleton } from '@/components/ui/skeleton';
import { userStore } from '@/stores/user.store';

import { USAGE_WARNINGS } from './hooks/use-credit-usage';
import {
  getLiteModeMessage,
  useOutOfCreditsCta,
} from './out-of-credits-banner';
import { useCreditBalanceQuery } from './subscription.query';
import { subscriptionStore } from './subscription.store';
import { daysUntil, formatResetDate, usedPct } from './usage-format';

const WARNING_PCT = USAGE_WARNINGS[0].pct;
const DANGER_PCT = USAGE_WARNINGS[USAGE_WARNINGS.length - 1].pct;

const usageFillClass = (pct: number) => {
  if (pct >= DANGER_PCT) return 'bg-danger-9';
  if (pct >= WARNING_PCT) return 'bg-warning-9';
  return undefined;
};

const formatCycleReset = (isoDate: string | undefined) => {
  if (!isoDate) return 'Resets when your plan renews';
  const days = daysUntil(isoDate);
  const relative =
    days === 0 ? 'today' : days === 1 ? 'tomorrow' : `in ${days} days`;
  return `Resets ${formatResetDate(isoDate)} · ${relative}`;
};

const UsageRow = ({
  title,
  pct,
  meta,
}: {
  title: string;
  pct: number;
  meta: string;
}) => (
  <YStack className="gap-2">
    <XStack className="items-baseline justify-between gap-4">
      <span className="text-sm text-foreground">{title}</span>
      <span className="text-sm tabular-nums text-foreground">{pct}% used</span>
    </XStack>
    <ProgressBar
      value={pct / 100}
      label={title}
      className="h-2"
      fillClassName={usageFillClass(pct)}
    />
    <span className="text-xs text-foreground-muted">{meta}</span>
  </YStack>
);

const LiteModeRow = observer(() => {
  const { cta, run } = useOutOfCreditsCta();
  return (
    <XStack className="items-center justify-between gap-3 rounded-lg bg-warning-3 px-3 py-2">
      <XStack className="min-w-0 items-center gap-2">
        <span className="size-1.5 shrink-0 rounded-full bg-warning-9" />
        <span className="truncate text-xs text-warning-11">
          {getLiteModeMessage()}
        </span>
      </XStack>
      {cta.kind !== 'none' && (
        <Button
          size="xs"
          variant="outline"
          className="shrink-0 bg-surface"
          onClick={run}
        >
          {cta.label}
        </Button>
      )}
    </XStack>
  );
});

const OverageRow = ({ credits }: { credits: number }) => (
  <XStack className="items-center justify-between gap-4 border-t border-border pt-4">
    <YStack className="gap-1">
      <span className="text-sm text-foreground">Overage this period</span>
      <p className="text-xs text-foreground-muted">
        Usage beyond your allowances. Internal accounts are never blocked.
      </p>
    </YStack>
    <span className="text-sm tabular-nums text-foreground">
      {credits.toLocaleString()} credits
    </span>
  </XStack>
);

const PlanUsageRowsSkeleton = () => (
  <YStack className="gap-2">
    <XStack className="justify-between">
      <Skeleton className="h-4 w-28" />
      <Skeleton className="h-4 w-16" />
    </XStack>
    <Skeleton className="h-2 w-full rounded-full" />
    <Skeleton className="h-3 w-40" />
  </YStack>
);

export const PlanUsageRows = observer(() => {
  const { data } = useCreditBalanceQuery();

  if (!data) return <PlanUsageRowsSkeleton />;

  const {
    plan_balance,
    plan_grant_amount,
    plan_grant_kind,
    daily_balance,
    daily_grant_amount,
    overage_credits,
    cycle_resets_at,
  } = data;

  const hasDaily = daily_grant_amount > 0 || daily_balance > 0;
  const isOneTimePlan = plan_grant_kind === 'one_time';
  const showPlan =
    plan_grant_kind === 'monthly' || (isOneTimePlan && plan_balance > 0);

  return (
    <>
      {!hasDaily && !showPlan && (
        <span className="text-xs text-foreground-muted">
          Your plan has no usage allowance right now.
        </span>
      )}
      {hasDaily && (
        <UsageRow
          title="Daily usage"
          pct={usedPct(daily_balance, daily_grant_amount)}
          meta="Resets every day · Spent first"
        />
      )}
      {showPlan && (
        <UsageRow
          title={isOneTimePlan ? 'Welcome usage' : 'Monthly usage'}
          pct={usedPct(plan_balance, plan_grant_amount)}
          meta={
            isOneTimePlan
              ? 'One-time · Spent after your daily usage'
              : formatCycleReset(cycle_resets_at)
          }
        />
      )}
      {subscriptionStore.isLiteModeActive && <LiteModeRow />}
      {userStore.isInternalUser && (
        <OverageRow credits={overage_credits ?? 0} />
      )}
    </>
  );
});
